import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from '../useDebounce';
import { QueryState } from '@common/types/queryState';

export const useSearch = (
    urlValue: string,
    setUrl: (update: Partial<QueryState>) => void,
    delay: number = 0,
) => {
    const [localValue, setLocalValue] = useState(urlValue);

    useEffect(() => setLocalValue(urlValue), [urlValue]);

    const setDebouncedValue = useDebounce(
        (search: string) => setUrl({ search, pageIndex: 0 }),
        delay,
    );

    const onChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.currentTarget.value;
            setLocalValue(val);
            setDebouncedValue(val);
        },
        [setDebouncedValue],
    );

    const onClear = useCallback(
        () => setUrl({ search: '', pageIndex: 0 }),
        [setUrl],
    );

    const onSubmit = useCallback(
        (search: string) => setUrl({ search, pageIndex: 0 }),
        [setUrl],
    );

    return useMemo(
        () => ({
            value: localValue,
            onChange,
            onClear,
            onSubmit,
        }),
        [localValue, onChange, onClear, onSubmit],
    );
};

export type UseSearchResult = ReturnType<typeof useSearch>;
